/**
 * PartnerBadgeCard — Gold gradient badge circle with download CTA.
 *
 * Visual:
 *   Gold LinearGradient circle (#FBBF24 → #F59E0B → #D97706) with outer ring
 *   ribbon-outline icon + "FOUNDING\nPRACTITIONER" text inside
 *   "Founding Practitioner" title below
 *   "Certified since {{date}}" subtitle
 *   Body copy about lifetime commission rate
 *   OutlineButton "Download Badge"
 *
 * Center-aligned. Wrapped in GlassCard withChromeBorder={false}.
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { Ionicons } from '@expo/vector-icons';
import { GlassCard } from '../../glass/GlassCard';
import { OutlineButton } from '../../ui/OutlineButton';
import { createBoxShadow } from '../../../lib/design-tokens';

// ---------------------------------------------------------------------------
// Module-level constants
// ---------------------------------------------------------------------------

const GOLD_GRADIENT_COLORS = ['#FBBF24', '#F59E0B', '#D97706'] as const;
const GOLD_GRADIENT_START = { x: 0, y: 0 };
const GOLD_GRADIENT_END = { x: 1, y: 1 };

const BADGE_CIRCLE_SIZE = 96;
const BADGE_RING_SIZE = 104;
const BADGE_RING_COLOR = 'rgba(251,191,36,0.20)';

const BADGE_GLOW = createBoxShadow({
  color: '#F59E0B',
  offsetX: 0,
  offsetY: 0,
  blurRadius: 24,
  opacity: 0.45,
  elevation: 0,
});

const DOWNLOAD_ACCENT_COLOR = '#7C3AED';

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

interface PartnerBadgeCardProps {
  certifiedSince: string;
  onDownload: () => void;
  isDownloading?: boolean;
  /** i18n labels */
  foundingTitle: string;
  foundingBodyText: string;
  downloadLabel: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PartnerBadgeCard: React.FC<PartnerBadgeCardProps> = ({
  certifiedSince,
  onDownload,
  isDownloading = false,
  foundingTitle,
  foundingBodyText,
  downloadLabel,
}) => {
  return (
    <GlassCard withChromeBorder={false} style={styles.card}>
      {/* Badge circle with outer ring */}
      <View style={styles.badgeRingWrapper}>
        <LinearGradient
          colors={GOLD_GRADIENT_COLORS}
          start={GOLD_GRADIENT_START}
          end={GOLD_GRADIENT_END}
          style={[styles.badgeCircle, BADGE_GLOW as ViewStyle]}
        >
          <Ionicons name="ribbon-outline" size={32} color="#FFFFFF" />
          <Text style={styles.badgeText}>{'FOUNDING\nPRACTITIONER'}</Text>
        </LinearGradient>
      </View>

      {/* Title */}
      <Text style={styles.title}>{foundingTitle}</Text>

      {/* Certified since */}
      <Text style={styles.subtitle}>{certifiedSince}</Text>

      {/* Body copy */}
      <Text style={styles.body}>{foundingBodyText}</Text>

      {/* Download button */}
      <OutlineButton
        label={downloadLabel}
        onPress={onDownload}
        loading={isDownloading}
        fullWidth={true}
        accentColor={DOWNLOAD_ACCENT_COLOR}
        iconLeft={<Ionicons name="download-outline" size={16} color={DOWNLOAD_ACCENT_COLOR} />}
        style={styles.downloadButton}
      />
    </GlassCard>
  );
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  card: {
    alignItems: 'center',
  } as ViewStyle,
  badgeRingWrapper: {
    width: BADGE_RING_SIZE,
    height: BADGE_RING_SIZE,
    borderRadius: BADGE_RING_SIZE / 2,
    borderWidth: 4,
    borderColor: BADGE_RING_COLOR,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  } as ViewStyle,
  badgeCircle: {
    width: BADGE_CIRCLE_SIZE,
    height: BADGE_CIRCLE_SIZE,
    borderRadius: BADGE_CIRCLE_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
  } as ViewStyle,
  badgeText: {
    fontSize: 8,
    fontWeight: '800',
    color: '#FFFFFF',
    textAlign: 'center',
    lineHeight: 10,
    paddingHorizontal: 8,
  } as TextStyle,
  title: {
    fontSize: 18,
    fontWeight: '800',
    color: '#18181B',
    textAlign: 'center',
  } as TextStyle,
  subtitle: {
    fontSize: 13,
    fontWeight: '400',
    color: '#71717A',
    textAlign: 'center',
    marginTop: 4,
  } as TextStyle,
  body: {
    fontSize: 13,
    fontWeight: '400',
    color: '#52525B',
    textAlign: 'center',
    maxWidth: 264,
    marginTop: 12,
  } as TextStyle,
  downloadButton: {
    marginTop: 20,
    alignSelf: 'stretch',
  } as ViewStyle,
});
